// PostHog events for the Draft Plan.
//
// Mirrors src/utils/liveBoardAnalytics.js and src/utils/mockAnalytics.js: the
// event name and its property shape live in one place, posthog-js is imported
// once, and no PII ever lands in properties. PostHog itself is initialized in
// src/App.js.
//
// ══════════════════════════════════════════════════════════════════════
// 🔴 THIS SURFACE SHIPPED WITH NO ANALYTICS AT ALL, AND THAT WAS NOT A
// DELIBERATE MINIMUM — IT WAS AN OMISSION.
//
// Draft Plan went out as a public beta on aug27 as appMode "plan". It has its
// own landing card and its own header tab, but it was never added to
// FEATURE_BY_MODE in AuctionDraftBuilder, it has no route of its own, and
// nothing under components/DraftPlan/ ever called capture. The result was that
// on aug28 the question "how many people are using Draft Plan" had no answer
// available at any price — not a small number, no number.
//
// ⚠️ AND THE NEAR MISS IS WORTH NAMING: the `team_builder` feature_view label
// belongs to the OLD Team Builder (appMode "builder"), which is a DIFFERENT
// SECTION that still ships beside this one. It runs 30-50 people a day and it
// predates Draft Plan by two weeks. Reading it as Draft Plan adoption is the
// obvious mistake and it is wrong in the flattering direction. Draft Plan is
// `draft_plan`; nothing else is.
// ══════════════════════════════════════════════════════════════════════
//
// 🔴 NOTHING HERE MAY CARRY A NAME. Plan names are typed by the user and are
// routinely a person's league, a rival's team, or a joke about a friend. The
// player names in a plan are public facts, but a PLAN is a private document —
// it is the one place on this site where someone writes down what they intend
// to do. Counts, objectives and totals only.

import posthog from "posthog-js";

// Analytics must never break a user flow — swallow capture failures, the same
// rule every other capture module on this site follows.
function safeCapture(event, properties) {
  try {
    posthog.capture(event, properties);
  } catch {
    /* ignore */
  }
}

/**
 * Fired when a plan is created — the New and Duplicate paths both, separated
 * by `source`, because they mean different things about the person: New is
 * someone starting, Duplicate is someone iterating on a plan they already care
 * enough about to fork.
 *
 * ⚠️ NOT FIRED FOR THE PLANS THE OPTIMIZER BUILDS. A single optimize run lands
 * four columns as four plans through createFilledPlans; counting those here
 * would report a burst of "creation" every time somebody pressed one button,
 * and would drown the hand-made plans that are the actual signal.
 * `plan_optimized` is what records that run, once.
 */
export function capturePlanCreated({ source, planCount }) {
  safeCapture("plan_created", {
    source: source ?? null,          // "new" | "duplicate"
    plan_count: Number.isFinite(planCount) ? planCount : null,
  });
}

/**
 * Fired once per optimizer run that actually produced plans.
 *
 * 🔴 slots_filled IS THE POINT OF THIS EVENT, not `mode` or `objective`.
 * planOptimizer returns `{ ok: true, assignments: [] }` when there is nothing
 * open to solve — a successful no-op — and the surface renders that as a
 * button that appears to do nothing. That is exactly the complaint Ben's own
 * manual test produced on aug18. A run with slots_filled: 0 is that bug
 * happening to a real person, and it is invisible without this field.
 */
export function capturePlanOptimized({ mode, objective, slotsFilled, lockedSlots }) {
  safeCapture("plan_optimized", {
    mode: mode ?? null,              // "remaining" | "commitments" | "scratch"
    objective: objective ?? null,    // "ppg" | "vorp"
    slots_filled: Number.isFinite(slotsFilled) ? slotsFilled : null,
    locked_slots: Number.isFinite(lockedSlots) ? lockedSlots : null,
  });
}

/**
 * Fired when the one-page sheet is sent to the printer.
 *
 * This is the strongest intent signal on the surface: a printed plan is one
 * somebody means to carry into a room. It is worth its own event even though
 * it will be the rarest of the three.
 *
 * ⚠️ window.print() is synchronous and blocking, and the capture goes FIRST so
 * the event is queued before the print dialog takes the thread. A user who
 * cancels the dialog still counts as having printed — that is a deliberate
 * over-count of intent, and the alternative (an afterprint listener) reports
 * nothing at all on the browsers that never fire it.
 */
export function capturePlanPrinted({ slotsFilled, planCount }) {
  safeCapture("plan_printed", {
    slots_filled: Number.isFinite(slotsFilled) ? slotsFilled : null,
    plan_count: Number.isFinite(planCount) ? planCount : null,
  });
}
